import React, { useState, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import CustomDropdown from "../../../../../components/CustomDropdown/CustomDropdown";

const MGMT_KEY = "novala.managementReports";

interface Props {
  open: boolean;
  onClose: () => void;
  accountId: string;
  accountName: string;
}

type Mode = "new" | "existing";

export default function AddToManagementReportsModal({ open, onClose, accountId, accountName }: Props) {
  const [mode, setMode] = useState<Mode>("new");
  const [newName, setNewName] = useState("");
  const [existingValue, setExistingValue] = useState<string>("");
  const [existing, setExisting] = useState<string[]>([]);

  useEffect(() => {
    if (!open) return;
    try {
      const raw = window.localStorage.getItem(MGMT_KEY);
      const list = raw ? JSON.parse(raw) : [];
      const names = Array.from(new Set((list as any[]).map((r) => r.name || r.accountName).filter(Boolean)));
      setExisting(names as string[]);
      setMode(names.length > 0 ? "new" : "new");
      setNewName("");
      setExistingValue("");
    } catch {
      setExisting([]);
    }
  }, [open]);

  const options = useMemo(() => existing.map((n) => ({ label: n, value: n })), [existing]);

  if (!open) return null;

  const hasExisting = existing.length > 0;
  const canSave = mode === "new" ? newName.trim().length > 0 : existingValue !== "";

  const handleAdd = () => {
    try {
      const raw = window.localStorage.getItem(MGMT_KEY);
      const list = raw ? JSON.parse(raw) : [];
      const targetName = mode === "new" ? newName.trim() : existingValue;
      list.push({
        id: `mr-${Date.now()}`,
        name: targetName,
        accountId,
        accountName,
        addedAt: new Date().toISOString(),
      });
      window.localStorage.setItem(MGMT_KEY, JSON.stringify(list));
    } catch {}
    onClose();
  };

  return createPortal(
    <div className="fixed inset-0 z-[9000] flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#EEF1F4]">
          <h2 className="text-lg font-bold text-ink">Add to management reports</h2>
          <button type="button" onClick={onClose} aria-label="Close" className="text-ink3 hover:text-ink"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-5 py-5">
          <p className="text-[13.5px] text-ink mb-4">Select an option to add <strong>{accountName} quick report</strong></p>
          <div className="flex gap-8 mb-5">
            <Radio label="Create a new management report" checked={mode === "new"} onChange={() => setMode("new")} />
            <Radio label="Add to an existing management report" checked={mode === "existing"} onChange={() => hasExisting && setMode("existing")} disabled={!hasExisting} />
          </div>
          {mode === "new" ? (
            <div>
              <label className="block text-[13px] text-ink mb-2 font-medium">Enter a name for the management report</label>
              <input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Enter a name" className="w-full h-[42px] border border-[#D8DEE6] rounded-lg px-3.5 text-[13.5px] bg-white focus:outline-none focus:border-teal-dark focus:ring-2 focus:ring-teal-dark/10" />
              {!hasExisting && <p className="mt-1.5 text-[11.5px] text-ink3">You don't have any management reports yet. Create your first one here.</p>}
            </div>
          ) : (
            <div>
              <label className="block text-[13px] text-ink mb-2 font-medium">Choose an existing management report</label>
              <CustomDropdown
                value={existingValue}
                onChange={setExistingValue}
                options={options}
                placeholder="Select"
                searchable={existing.length > 6}
                searchPlaceholder="Search"
              />
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2.5 px-5 py-3 border-t border-[#EEF1F4] bg-[#F7F9FB]">
          <button type="button" onClick={onClose} className="h-[40px] px-5 border border-[#D8DEE6] rounded-[10px] text-[13.5px] font-semibold text-ink bg-white">Cancel</button>
          <button type="button" onClick={handleAdd} disabled={!canSave} className="h-[40px] px-5 border border-ink rounded-[10px] text-[13.5px] font-semibold text-white bg-ink disabled:opacity-40 disabled:cursor-not-allowed">Add</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function Radio({ label, checked, onChange, disabled }: { label: string; checked: boolean; onChange: () => void; disabled?: boolean }) {
  return (
    <label className={`inline-flex items-center gap-2.5 text-[13.5px] text-ink cursor-pointer select-none ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}>
      <span className={`relative w-5 h-5 rounded-full border-2 ${checked ? "border-teal-dark" : "border-[#D8DEE6]"} bg-white inline-flex items-center justify-center`}>
        {checked && <span className="w-2.5 h-2.5 rounded-full bg-teal-dark" />}
      </span>
      <input type="radio" className="sr-only" checked={checked} onChange={onChange} disabled={disabled} />
      {label}
    </label>
  );
}